import {Col, Row} from "../../components/Flex/Flex.tsx";
import {Paper, Select, Button, Switch} from "@mantine/core";
import {useEntityTypes} from "../../utils/api.ts";
import {useCallback, useMemo, useState} from "react";
import { ArrowClockwise as IconRefresh, XCircle as IconCircleX } from "@phosphor-icons/react";
import Label from "../../components/Label/Label.tsx";


export default function EntityStatCompare() {

    const {ships: list} = useEntityTypes(['ships'], true);

    const [reference, setReference] = useState<string>('');
    const [compare, setCompare] = useState<string[]>([]);
    const [mode, setMode] = useState<'absolute'|'percentage'>('absolute');


    const refShip = useMemo<ShipType | undefined>(() => {
        if (reference === '' || list.length === 0) {
            return undefined;
        }

        return list.find(s => s.id === reference);

    }, [reference, list]);

    const compShips = useMemo<ShipType[]>(() => {
        if (list.length === 0) {
            return [];
        }

        return compare.map(c => list.find(s => s.id === c)).filter(s => !!s);
    }, [compare, list]);

    const removeCompare = useCallback((id: string) => {
        setCompare(prev => prev.filter(c => c !== id))
    }, [])


    return (
        <div>
            <title>Compare ship stats</title>
            <Row align="center">
                <Col><h1>Compare ship stats</h1></Col>
                <Col grow={1}/>
            </Row>
            <Paper className="mb-3">
                <Row>
                    <Col>
                        <h2 className="mt-0 mb-4">Select Entity</h2>
                        <Select value={reference} onChange={(v) => setReference(v || '')} searchable
                                  data={list.map(e => ({value: e.id, label: e.name}))}/>
                    </Col>
                    <Col>
                        <h2 className="mt-0 mb-4">Compare with</h2>
                        <Select value="" onChange={(v) => {
                            if (v) setCompare(c => [...c, v])
                        }} searchable
                                  data={list.map(e => ({value: e.id, label: e.name}))}/>
                    </Col>
                    <Col grow={1} />
                    <Col>
                        <Row align="center">
                            <Switch id="percentage" checked={mode==='percentage'} onChange={(e) => setMode(e.currentTarget.checked ? 'percentage' : 'absolute')}/>
                            <Label htmlFor="percentage" className="m-0">Percentage</Label>
                        </Row>

                    </Col>
                    <Col><Button leftSection={<IconRefresh size={16}/>} onClick={() => {
                        setReference('');
                        setCompare([]);
                    }}>Reset</Button></Col>
                </Row>


                {refShip && <ShipCompareDisplay entity={refShip} compare={compShips} removeCompare={removeCompare} mode={mode}/>}

            </Paper>
        </div>
    )
}

const shipAttributes: StatRowConfig[] = [
    {label: "Hyper Speed", accessor: "speed.hyper", type: "number"},
    {label: "Sublight Speed", accessor: "speed.sublight", type: "number"},
    {label: "Planetary Speed", accessor: "speed.planetary", type: "number"},
    {label: "Manoeuvrability", accessor: "manoeuvrability", type: "number"},
    {label: "Sensors", accessor: "sensors", type: "number"},
    {label: "ECM", accessor: "ecm", type: "number"},
    {label: "Weight", accessor: "weight", type: "number", flipped: true},
    {label: "Volume", accessor: "volume", type: "number", flipped: true},
    {label: "Weight Capacity", accessor: "weightcap", type: "number"},
    {label: "Volume Capacity", accessor: "volumecap", type: "number"},
    {label: "Max Passengers", accessor: "maxpassengers", type: "number"},
    {label: "Length", accessor: "length", type: "number", flipped: true},
    {label: "Hull", accessor: "hull", type: "number"},
    {label: "Shield", accessor: "shield", type: "number"},
    {label: "Ionic Resistance", accessor: "ionic", type: "number"},
    {label: "Armour", accessor: "armour", type: "number"},
    {label: "Slot Size", accessor: "slotsize", type: "number"},
    {label: "Repulsors", accessor: "repulsors", type: "boolean"},
    {label: "Hangar Bay", accessor: "hangarbay", type: "boolean"},
    {label: "Docking Bay", accessor: "dockingbay", type: "boolean"},
];


function getValueByAccessor(obj: ShipType | StationType, accessor: string): string | number {
    // @ts-ignore
    return accessor.split('.').reduce((acc, key) => acc && acc[key], obj);
}


interface StatRowConfig {
    label: string, accessor: string, type: 'number' | 'boolean', flipped?: boolean
}
interface DiffRowProps {
    configuration: StatRowConfig;
    reference: ShipType
    compare: ShipType[]
    flipped?: boolean
    mode: 'absolute' | 'percentage'
}

const nf = new Intl.NumberFormat(undefined, {signDisplay: 'auto'});
const df = new Intl.NumberFormat(undefined, {signDisplay: 'always'});
const pc = new Intl.NumberFormat(undefined, {signDisplay: 'always', style: 'percent'})

function DiffRow({configuration, reference, compare, mode}: DiffRowProps) {
    let ref = getValueByAccessor(reference, configuration.accessor);

    if (configuration.type === 'boolean') {
        return (
            <tr>
                <th className="text-left">{configuration.label}</th>
                <td>{ref ? 'Y' : 'N'}</td>
                {compare.map(c => <DiffCell key={c.id} configuration={configuration} refVal={ref} entity={c} mode={mode}/>)}
            </tr>
        )
    }

    if (typeof ref !== 'number') {
        ref = parseFloat(ref)
    }


    return (
        <tr>
            <th className="text-left">{configuration.label}</th>
            <td>{nf.format(ref)}</td>
            {compare.map(c => <DiffCell key={c.id} configuration={configuration} refVal={ref} entity={c} mode={mode}/>)}
        </tr>
    )
}


const DiffCell = ({configuration, refVal, entity, mode}: {
    configuration: StatRowConfig,
    refVal: number|string,
    entity: ShipType,
    mode: 'absolute' | 'percentage'
}) => {
    let value = getValueByAccessor(entity, configuration.accessor);
    if (configuration.type === 'boolean') {
        let diff = (value ? 1 : 0) - (refVal ? 1 : 0);
        return <>
            <td>{value ? 'Y' : 'N'}</td>
            <td><span className={diff < 0 ? 'text-red' : 'text-green'}>
            {diff > 0 && 'Y'}
                {diff < 0 && 'N'}</span></td>
        </>
    }
    if (typeof value !== 'number') {
        value = parseFloat(value)
    }
    if (typeof refVal !== 'number') {
        refVal = parseFloat(refVal)
    }
    const diff = value - refVal;
    const perc = diff/(refVal === 0 ? 1 : refVal);
    const color = (configuration.flipped
        ? diff >= 0
        : diff < 0)
        ? 'text-red'
        : 'text-green';
    return (<>
        <td>{nf.format(value)}</td>
        <td>{diff !== 0 ? <span className={color}>{mode === 'absolute' ? df.format(diff) : pc.format(perc)}</span> : ''}</td>
    </>);

}

function ShipCompareDisplay({entity, compare, removeCompare, mode}: { entity: ShipType, compare: ShipType[], removeCompare: (id: string) => void, mode: 'absolute' | 'percentage' }) {

    return (
        <table className="w-full mt-4 striped">
            <thead>
            <tr>
                <th></th>
                <th>{entity.name}</th>
                {compare.map(c =>
                    <th key={c.id} colSpan={2}>
                        <IconCircleX size={16} style={{marginRight: '0.5rem', cursor: 'pointer', color: 'var(--mantine-color-red-5)'}} onClick={() => {
                            removeCompare(c.id);
                        }}/>
                        {c.name}
                    </th>)}
            </tr>
            </thead>
            <tbody className="text-right">
            {shipAttributes.map(row => <DiffRow key={row.label} configuration={row} reference={entity} compare={compare} mode={mode}/>)}
            </tbody>
            <WeaponsDiff entity={entity} compare={compare} />
        </table>
    )

}


type WeaponList = {
    [id: string]: WeaponListRow
}
type WeaponListRow = {
    name: string,
    reference: number,
    [shipId: string]: number|string
}

// @ts-ignore
function WeaponsDiff({entity, compare}: {entity: ShipType, compare: ShipType[]}) {
    const list: WeaponList = {};

    entity.weapons.forEach((w) => {
        if (typeof list[w.id] === 'undefined') {
            list[w.id] = {name: w.name, reference: 0};
            compare.forEach(c => {
                list[w.id][c.id] = 0;
            })
        }
        list[w.id].reference = w.qty;
    });
    compare.forEach(ship => {
        ship.weapons.forEach(w => {
            if (typeof list[w.id] === 'undefined') {
                list[w.id] = {name: w.name, reference: 0};
                compare.forEach(c => {
                    list[w.id][c.id] = 0;
                })
            }
            list[w.id][ship.id] = w.qty;
        });
    });

    return (
        <tbody className="text-right">
        <tr>
            <th colSpan={5} className="text-center">Weapons</th>
        </tr>
        {Object.values(list).map(row => <WeaponRow key={row.name} data={row} compare={compare}/>)}
        </tbody>
    )

}

function WeaponRow({data, compare}: {data: WeaponListRow, compare: ShipType[]}) {
    return (
        <tr>
            <th className="text-left">{data.name}</th>
            <td>{nf.format(data.reference)}</td>
            {compare.map(ship => <WeaponCell key={ship.id} reference={data.reference} data={data[ship.id]}/>)}
        </tr>
    )
}

function WeaponCell({reference, data}: { reference: number, data: number|string }) {
    const parsedData = typeof data === 'string' ? parseFloat(data) : data;
    const diff = parsedData-reference;
    const color = diff < 0 ? 'text-red' : 'text-green';
    return <><td>{nf.format(parsedData)}</td><td><span className={color}>{diff !== 0 ? df.format(diff) : ''}</span></td></>;
}
